import { ComponentWithLegacyClassNameProps } from '../../util/ComponentWithLegacyClassName/props';
import { PaletteColor } from '../../util/colors';
import { FontWeight, TextDecoration, TextSize } from '../../util/styles/fonts';

export { FontWeight, TextDecoration, TextSize };
export type { PaletteColor };

export enum TextColor {
  /**
   * Primary black text color
   */
  Primary = 'primary',

  /**
   * Secondary dark gray text color
   */
  Secondary = 'secondary',

  /**
   * Accent color for mentions, topics, etc.
   */
  Accent = 'accent',

  /**
   * Tertiary color for metadata
   */
  Metadata = 'metadata',

  /*
   * For use on darker backgrounds.
   */
  White = 'white',

  /**
   * For use on darker backgrounds. Note: This will always be white, even in dark mode.
   */
  StaticWhite = 'staticWhite',

  /**
   * For use in error messages.
   */
  Error = 'error',

  /**
   * For use in interaction elements which have become disabled.
   */
  Disabled = 'disabled',
}

export interface TextProps extends ComponentWithLegacyClassNameProps {
  /**
   * Text color
   */
  readonly color?: TextColor;

  /**
   * A preset size which determines a font-size + line-height combination
   * supporting our vertical rhythm.
   */
  readonly size?: TextSize;

  /**
   * Sets font-weight.
   */
  readonly fontWeight?: FontWeight;

  /**
   * Sets font-style: italic.
   */
  readonly italic?: boolean;

  /**
   * Sets text to uppercase.
   */
  readonly uppercase?: boolean;

  /**
   * Sets text-decoration.
   *
   * Note: If you intend to use `textDecoration.underline` to represent a clickable element,
   * instead consider using the `Clickable` or `FakeLink` component.
   */
  readonly textDecoration?: TextDecoration;

  /**
   * Background color.
   */
  readonly backgroundColor?: PaletteColor;

  /**
   * If provided, will hide child content from screenreaders using aria-hidden while making
   * the given screenreaderText available to screenreaders.
   */
  readonly screenreaderText?: string;

  /**
   * If provided, sets the lang= attribute to specify the language of the content
   */
  readonly language?: string;

  /**
   * Sets dir attribute on element. Use 'auto' for user-entered content where the language
   * isn't known (eg user and group names), or ltr/rtl where language is known (eg message
   * titles, bodies). Note that ltr/rtl do not change text alignment, only the layout
   * direction within the span.
   */
  readonly dir?: 'auto' | 'ltr' | 'rtl';
}
